import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Avatar,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Paper,
  TableContainer,
  TablePagination,
  InputAdornment,
  Chip,
  Snackbar,
  Alert,
} from '@mui/material';
import { Person as PersonIcon, Edit as EditIcon, Visibility as VisibilityIcon, Delete as DeleteIcon, Search as SearchIcon } from '@mui/icons-material';

const INITIAL_DATA = [
  { id: 1, name: 'Muhammad Kashan', role: 'Manager', email: 'kashan.k@restaurant.com', phone: '+1 (555) 123-4567', joined: '2022-04-12' },
  { id: 2, name: 'Liam Carter', role: 'Chef', email: 'liam.c@restaurant.com', phone: '+1 (555) 987-6543', joined: '2021-08-23' },
  { id: 3, name: 'Sophia Nguyen', role: 'Sous Chef', email: 'sophia.n@restaurant.com', phone: '+1 (555) 222-3344', joined: '2023-02-01' },
  { id: 4, name: 'Noah Brown', role: 'Server', email: 'noah.b@restaurant.com', phone: '+1 (555) 444-7788', joined: '2020-11-05' },
];

export default function StaffManagement() {
  const [rows, setRows] = useState(INITIAL_DATA);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [openView, setOpenView] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [selected, setSelected] = useState(null);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });
  const fileInputRef = useRef(null);

  const handleView = (row) => {
    setSelected(row);
    setOpenView(true);
  };

  const handleEdit = (row) => {
    setSelected(row);
    setOpenEdit(true);
  };

  const handleAdd = () => {
    const newRow = { id: Date.now(), name: '', role: '', email: '', phone: '', joined: new Date().toISOString().split('T')[0] };
    setSelected(newRow);
    setOpenEdit(true);
  };

  const openFilePicker = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const parseCSVLine = (line) => {
    const result = [];
    let cur = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQuotes && line[i+1] === '"') { cur += '"'; i++; } else { inQuotes = !inQuotes; }
      } else if (ch === ',' && !inQuotes) {
        result.push(cur);
        cur = '';
      } else {
        cur += ch;
      }
    }
    result.push(cur);
    return result.map(r => r.trim());
  };

  const parseCSV = (text) => {
    const lines = text.split(/\r?\n/).filter(l => l.trim().length > 0);
    if (!lines.length) return [];
    const headers = parseCSVLine(lines[0]).map(h => h.trim().toLowerCase());
    const data = [];
    for (let i = 1; i < lines.length; i++) {
      const cols = parseCSVLine(lines[i]);
      if (cols.length === 0) continue;
      const obj = {};
      for (let j = 0; j < headers.length; j++) {
        obj[headers[j]] = cols[j] ?? '';
      }
      data.push(obj);
    }
    return data;
  };

  const handleFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setSnack({ open: true, message: 'Please upload a .csv file', severity: 'error' });
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const text = ev.target.result;
        const parsed = parseCSV(text);
        if (!parsed.length) {
          setSnack({ open: true, message: 'CSV is empty or invalid', severity: 'error' });
          return;
        }
        const mapped = parsed.map((r, idx) => ({
          id: Date.now() + idx,
          name: r.name || r.fullname || r['full name'] || '',
          role: r.role || r.position || '',
          email: r.email || '',
          phone: r.phone || r.telephone || '',
          joined: r.joined || r.join_date || new Date().toISOString().split('T')[0],
        }));
        setRows(prev => [...mapped, ...prev]);
        setSnack({ open: true, message: `Imported ${mapped.length} staff records`, severity: 'success' });
      } catch (err) {
        setSnack({ open: true, message: 'Error parsing CSV', severity: 'error' });
      }
    };
    reader.readAsText(file);
    e.target.value = null;
  };

  const handleDelete = (row) => {
    if (window.confirm(`Delete ${row.name}? This cannot be undone.`)) {
      setRows(prev => prev.filter(r => r.id !== row.id));
    }
  };

  const handleSave = () => {
    // simple local update for demo
    setRows(prev => {
      const exists = prev.find(r => r.id === selected.id);
      if (exists) return prev.map(r => r.id === selected.id ? selected : r);
      return [selected, ...prev];
    });
    setOpenEdit(false);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filterRows = (rows) => rows.filter(r => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return [r.name, r.role, r.email, r.phone].some(field => String(field || '').toLowerCase().includes(q));
  });

  const displayed = filterRows(rows);
  const paged = displayed.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box sx={{ mt: 3 }}>
      <Paper elevation={2} sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h6" fontWeight={700}>Staff Management</Typography>
            <Typography variant="caption" color="text.secondary">Manage staff members, roles and contact info</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Search staff, role, email..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ width: { xs: 160, sm: 240 } }}
            />
            <Button variant="contained" color="primary" startIcon={<PersonIcon />} onClick={handleAdd}>Add Staff</Button>
            <input ref={fileInputRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={handleFileChange} />
            <Button variant="outlined" color="primary" onClick={openFilePicker}>Upload CSV</Button>
          </Box>
        </Box>

        <TableContainer>
          <Table size="medium">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Joined</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paged.map((row) => (
                <TableRow key={row.id} hover sx={{ '&:hover': { boxShadow: 3 } }}>
                  <TableCell>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Avatar sx={{ bgcolor: 'primary.main', width: 44, height: 44, fontSize: 14 }}>{row.name.split(' ').map(n=>n[0]).slice(0,2).join('')}</Avatar>
                      <Box>
                        <Typography fontWeight={600}>{row.name || '—'}</Typography>
                        <Typography variant="caption" color="text.secondary">{row.email}</Typography>
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Chip label={row.role} size="small" color={row.role.toLowerCase().includes('chef') ? 'default' : 'default'} />
                  </TableCell>
                  <TableCell>{row.email}</TableCell>
                  <TableCell>{row.phone}</TableCell>
                  <TableCell>{new Date(row.joined).toLocaleDateString()}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="View">
                      <IconButton size="small" onClick={() => handleView(row)} sx={{ mr: 0.5 }}>
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => handleEdit(row)} sx={{ mr: 0.5 }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" onClick={() => handleDelete(row)}>
                        <DeleteIcon fontSize="small" color="error" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={displayed.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5,10,25]}
        />
      </Paper>

      <Dialog open={openView} onClose={() => setOpenView(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Staff Details</DialogTitle>
        <DialogContent>
          {selected && (
            <Box sx={{ display: 'grid', gap: 1 }}>
              <Typography><strong>Name:</strong> {selected.name}</Typography>
              <Typography><strong>Role:</strong> {selected.role}</Typography>
              <Typography><strong>Email:</strong> {selected.email}</Typography>
              <Typography><strong>Phone:</strong> {selected.phone}</Typography>
              <Typography><strong>Joined:</strong> {new Date(selected.joined).toLocaleDateString()}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenView(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openEdit} onClose={() => setOpenEdit(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Staff</DialogTitle>
        <DialogContent>
          {selected && (
            <Box sx={{ display: 'grid', gap: 2, mt: 1 }}>
              <TextField label="Name" value={selected.name} onChange={(e) => setSelected({...selected, name: e.target.value})} fullWidth />
              <TextField label="Role" value={selected.role} onChange={(e) => setSelected({...selected, role: e.target.value})} fullWidth />
              <TextField label="Email" value={selected.email} onChange={(e) => setSelected({...selected, email: e.target.value})} fullWidth />
              <TextField label="Phone" value={selected.phone} onChange={(e) => setSelected({...selected, phone: e.target.value})} fullWidth />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEdit(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>Save</Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(s => ({ ...s, open: false }))}>
        <Alert onClose={() => setSnack(s => ({ ...s, open: false }))} severity={snack.severity} sx={{ width: '100%' }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
